import Button from '@components/Button';
import useTranslation from '@hooks/useTranslation';
import type { ModifiersType } from '@utils';
import { createClsNameMap, mapModifiers } from '@utils';
import type { PropsWithChildren } from 'react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export const clsNameMap = createClsNameMap({
  elements: {
    backdrop: createClsNameMap(),
    container: createClsNameMap(),
    wrapper: createClsNameMap(),
    open: createClsNameMap(),
    'last-child': createClsNameMap(),
    'close-btn': createClsNameMap({
      modifiers: ['on-overlay'],
    }),
  },
})('modal');

export type ModalProps = {
  isOpen: boolean;
  hasCloseButton?: boolean;
  type: 'cart' | 'quickview';
  handleCloseModal: () => void;
  closeBtnModifiers?: ModifiersType<(typeof clsNameMap)['close-btn'], true>;
};

const Modal = ({
  isOpen,
  children,
  hasCloseButton,
  type,
  handleCloseModal,
  closeBtnModifiers,
  ...props
}: PropsWithChildren<ModalProps>) => {
  const { t } = useTranslation();

  const modalWrapperRef = useRef<HTMLDivElement>(null);

  const handleClickBackDrop = (e: React.MouseEvent<HTMLDivElement>) => {
    handleCloseModal();
  };

  const preventFocusOutsideModalByTab = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab') modalWrapperRef.current?.focus();
  };

  const handleCloseModalByEscapeKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') handleCloseModal();
  };

  useEffect(() => {
    const body = document.body;
    const modalOpenCls = `modal-open-${type}`;

    if (isOpen) {
      modalWrapperRef.current?.focus();
      body.classList.add(modalOpenCls);
    } else {
      body.classList.remove(modalOpenCls);
    }

    return () => {
      body.classList.remove(modalOpenCls);
    };
  }, [isOpen]);
  return createPortal(
    <>
      {isOpen && (
        <div className={clsNameMap.root()} aria-modal='true' role='dialog' aria-label='modal'>
          <div className={clsNameMap.elm('backdrop')} onClick={handleClickBackDrop}></div>
          <div className={clsNameMap.elm('container')}>
            <div
              tabIndex={-1}
              ref={modalWrapperRef}
              className={clsNameMap.elm('wrapper')}
              onKeyUp={handleCloseModalByEscapeKey}
            >
              {children}
              {hasCloseButton && (
                <Button
                  className={mapModifiers(clsNameMap['close-btn'], closeBtnModifiers)}
                  aria-label={t('quickView.qvCloseModal')}
                  modifiers={['transparent', 'float']}
                  onClick={handleCloseModal}
                >
                  {type === 'cart' ? (
                    <svg
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        fill-rule='evenodd'
                        clip-rule='evenodd'
                        d='M3.29289 3.29289C3.68342 2.90237 4.31658 2.90237 4.70711 3.29289L12 10.5858L19.2929 3.29289C19.6834 2.90237 20.3166 2.90237 20.7071 3.29289C21.0976 3.68342 21.0976 4.31658 20.7071 4.70711L13.4142 12L20.7071 19.2929C21.0976 19.6834 21.0976 20.3166 20.7071 20.7071C20.3166 21.0976 19.6834 21.0976 19.2929 20.7071L12 13.4142L4.70711 20.7071C4.31658 21.0976 3.68342 21.0976 3.29289 20.7071C2.90237 20.3166 2.90237 19.6834 3.29289 19.2929L10.5858 12L3.29289 4.70711C2.90237 4.31658 2.90237 3.68342 3.29289 3.29289Z'
                        fill='#31373D'
                      />
                    </svg>
                  ) : (
                    <svg
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        fill-rule='evenodd'
                        clip-rule='evenodd'
                        d='M19.3536 5.35355C19.5488 5.15829 19.5488 4.84171 19.3536 4.64645C19.1583 4.45118 18.8417 4.45118 18.6464 4.64645L12 11.2929L5.35355 4.64645C5.15829 4.45118 4.84171 4.45118 4.64645 4.64645C4.45118 4.84171 4.45118 5.15829 4.64645 5.35355L11.2929 12L4.64645 18.6464C4.45118 18.8417 4.45118 19.1583 4.64645 19.3536C4.84171 19.5488 5.15829 19.5488 5.35355 19.3536L12 12.7071L18.6464 19.3536C18.8417 19.5488 19.1583 19.5488 19.3536 19.3536C19.5488 19.1583 19.5488 18.8417 19.3536 18.6464L12.7071 12L19.3536 5.35355Z'
                        fill='#222222'
                      />
                    </svg>
                  )}
                </Button>
              )}
              <div
                tabIndex={0}
                className={clsNameMap.elm('last-child')}
                onKeyUp={preventFocusOutsideModalByTab}
                onKeyDown={preventFocusOutsideModalByTab}
              />
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default Modal;
